import { useRef, useState } from "react";
import { useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { useGroceryStore } from "@/lib/grocery-store";
import { clearAccountScopedClientState } from "@/lib/clear-client-state";
import { useSignOutRecovery } from "@/components/web-sign-out-recovery";

/** Keep every web sign-out entry point on the same privacy and retry path. */
export function useWebSignOut(
  redirectUrl = `${import.meta.env.BASE_URL.replace(/\/$/, "")}/`,
) {
  const clerk = useClerk();
  const { signOut } = clerk;
  const setRecovery = useSignOutRecovery();
  const ownerId = useRef(clerk.user?.id);
  const queryClient = useQueryClient();
  const grocery = useGroceryStore();
  const signingOutRef = useRef(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);

  const handleSignOut = async () => {
    if (signingOutRef.current) return;
    // A provider failure can outlive this route. Do not revoke a replacement
    // member's session if the old recovery callback is invoked afterwards.
    if (ownerId.current && clerk.user && clerk.user.id !== ownerId.current) {
      setRecovery?.(null);
      return;
    }
    signingOutRef.current = true;
    setIsSigningOut(true);
    setSignOutError(false);
    setRecovery?.({ ownerId: ownerId.current, pending: true, failed: false, retry: handleSignOut });
    try {
      grocery.reset();
      queryClient.clear();
      clearAccountScopedClientState();
      await signOut({ redirectUrl });
      setRecovery?.(null);
    } catch {
      setSignOutError(true);
      setRecovery?.({ ownerId: ownerId.current, pending: false, failed: true, retry: handleSignOut });
    } finally {
      signingOutRef.current = false;
      setIsSigningOut(false);
    }
  };

  return { handleSignOut, isSigningOut, signOutError };
}
